import React, { useState } from 'react';
import {
  Newspaper,
  FileText,
  Package,
  MessageSquare,
  Users,
  Wrench,
  Building2,
  ChevronDown,
  ArrowLeft,
  Search,
  Plus,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Calendar,
  Eye,
  Trash2,
  Edit,
  Download,
  Share2,
  Mail,
  AlertCircle,
  Check,
  MapPin,
  Phone,
  User,
  Shield,
  Layers,
  ChevronRight,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { AnnouncementCategory } from '../../types';
import { AdminNewsManagementView } from './AdminNewsManagementView';
import { AdminSurveyManagementView } from './AdminSurveyManagementView';
import { AdminMailParcelManagementView } from './AdminMailParcelManagementView';
import { AdminComplaintsManagementView } from './AdminComplaintsManagementView';

export type MembersAffairsSubView =
  | 'menu'
  | 'announcements'
  | 'surveys'
  | 'parcels'
  | 'complaints'
  | 'edm'
  | 'repairs_legacy'
  | 'repairs'
  | 'facilities'
  | 'committee';

interface SurveyItem {
  id: string;
  title: string;
  description: string;
  category: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'closed' | 'draft';
  totalResponses: number;
  totalEligible: number;
  options: { label: string; votes: number }[];
}

interface EdmCampaign {
  id: string;
  subject: string;
  targetGroup: string;
  sentDate: string;
  status: 'sent' | 'scheduled' | 'draft';
  openedCount: number;
  totalRecipients: number;
  channel: 'Email' | 'LINE Official' | 'SMS' | 'App Push';
}

interface LegacyRepairItem {
  id: string;
  legacyCode: string;
  houseNumber: string;
  residentName: string;
  title: string;
  category: string;
  technician: string;
  cost: number;
  completedDate: string;
  note: string;
}

interface FacilityItem {
  id: string;
  name: string;
  type: string;
  capacity: string;
  openHours: string;
  status: 'available' | 'maintenance' | 'booked';
  fee: string;
  location: string;
  bookingsToday: number;
}

interface CommitteeMeeting {
  id: string;
  meetingNo: string;
  title: string;
  date: string;
  time: string;
  venue: string;
  attendeesCount: number;
  status: 'upcoming' | 'completed' | 'draft';
  resolutionsCount: number;
  agendaItems: string[];
}

export const AdminMembersAffairsView: React.FC = () => {
  const {
    announcements,
    parcels,
    markParcelPickedUp,
    maintenanceTickets,
    updateMaintenanceStatus,
  } = useApp();

  const [currentView, setCurrentView] = useState<MembersAffairsSubView>('menu');

  // Dropdown states for the menu
  const [repairsDropdownOpen, setRepairsDropdownOpen] = useState(false);
  const [facilitiesDropdownOpen, setFacilitiesDropdownOpen] = useState(false);
  const [topNavRepairsOpen, setTopNavRepairsOpen] = useState(false);
  const [topNavFacilitiesOpen, setTopNavFacilitiesOpen] = useState(false);

  // 2. Surveys state
  const [surveys] = useState<SurveyItem[]>([
    {
      id: 'srv-1',
      title: 'สำรวจความพึงพอใจการบริหารจัดการงานนิติบุคคล ประจำไตรมาส 1/2026',
      description: 'เพื่อนำความคิดเห็นและข้อเสนอแนะของเจ้าของร่วมทุกท่านมาพัฒนาปรับปรุงคุณภาพการบริการ',
      category: 'การบริการ & ความพึงพอใจ',
      startDate: '01/01/2026',
      endDate: '31/03/2026',
      status: 'active',
      totalResponses: 142,
      totalEligible: 280,
      options: [
        { label: 'พึงพอใจมากที่สุด (5 ดาว)', votes: 85 },
        { label: 'พึงพอใจมาก (4 ดาว)', votes: 38 },
        { label: 'ปานกลาง (3 ดาว)', votes: 14 },
        { label: 'ควรปรับปรุง (1-2 ดาว)', votes: 5 },
      ],
    },
    {
      id: 'srv-2',
      title: 'แบบสำรวจความคิดเห็น: โครงการติดตั้งสถานีชาร์จรถยนต์ไฟฟ้า (EV Charging Station)',
      description: 'ขอความคิดเห็นเกี่ยวกับการจัดสรรพื้นที่ส่วนกลางบริเวณคลับเฮาส์เพื่อติดตั้งจุดชาร์จ EV แบบ AC/DC',
      category: 'การพัฒนาส่วนกลาง',
      startDate: '15/02/2026',
      endDate: '15/03/2026',
      status: 'active',
      totalResponses: 198,
      totalEligible: 280,
      options: [
        { label: 'เห็นด้วยอย่างยิ่ง และต้องการใช้งาน', votes: 124 },
        { label: 'เห็นด้วย แต่ไม่มีแผนใช้ใน 1 ปีนี้', votes: 52 },
        { label: 'ไม่เห็นด้วย เกรงปัญหาที่จอดรถแออัด', votes: 18 },
        { label: 'ขอข้อมูลเพิ่มเติมเรื่องอัตราค่าบริการ', votes: 4 },
      ],
    },
    {
      id: 'srv-3',
      title: 'แบบสำรวจเวลาเปิด-ปิดสระว่ายน้ำและห้องออกกำลังกายฟิตเนส',
      description: 'ข้อเสนอขอขยายเวลาปิดบริการจากเดิม 21:00 น. เป็น 22:30 น.',
      category: 'ระเบียบการใช้สถานที่',
      startDate: '01/01/2026',
      endDate: '15/01/2026',
      status: 'closed',
      totalResponses: 215,
      totalEligible: 280,
      options: [
        { label: 'เห็นชอบให้ขยายเวลาเป็น 22:30 น.', votes: 168 },
        { label: 'คงเวลาเดิม 21:00 น. เพื่อความสงบยามค่ำคืน', votes: 47 },
      ],
    },
  ]);

  // 3. Complaints state
  const [complaints] = useState([
    {
      id: 'cmp-1',
      ticketNo: 'CMP-2026-0042',
      houseNumber: '1086/14',
      residentName: 'คุณวราภรณ์ สุวรรณเวช',
      category: 'เสียงรบกวน',
      title: 'สุนัขบ้านข้างเคียงส่งเสียงเห่ายามวิกาล',
      details: 'บ้านแปลงหัวมุมปล่อยสุนัขเห่าต่อเนื่องหลังเวลา 23:00 น. รบกวนการพักผ่อนของลูกบ้านในซอย 3',
      status: 'in_progress',
      createdAt: '15/08/2026 21:40',
      assignedTo: 'รปภ. หัวหน้ากะ / นิติบุคคล',
      resolution: 'เจ้าหน้าที่เข้าเจรจากับเจ้าของบ้านเรียบร้อยแล้ว เจ้าของยินดีนำสุนัขเข้าในบ้านก่อน 21:00 น.',
    },
    {
      id: 'cmp-2',
      ticketNo: 'CMP-2026-0039',
      houseNumber: '1086/08',
      residentName: 'คุณธนกฤต เลิศพิบูลย์',
      category: 'การจอดรถ',
      title: 'รถยนต์ของผู้มาติดต่อจอดขวางหน้าประตูรั้วบ้าน',
      details: 'มีรถเก๋งสีบรอนซ์เงินจอดขวางทางเข้า-ออก ทำให้ไม่สามารถนำรถออกจากบ้านไปทำงานได้',
      status: 'resolved',
      createdAt: '12/08/2026 08:15',
      assignedTo: 'เจ้าหน้าที่ รปภ.',
      resolution: 'รปภ. ได้ประสานงานติดต่อเจ้าของรถให้มาขยับย้ายรถออก พร้อมออกใบตักเตือนการจอดรถขวางประตู',
    },
    {
      id: 'cmp-3',
      ticketNo: 'CMP-2026-0035',
      houseNumber: '1086/22',
      residentName: 'คุณกิตติศักดิ์ ชัยชนะ',
      category: 'ขยะและกลิ่น',
      title: 'ขอให้เจ้าหน้าที่ฉีดล้างถังขยะส่วนกลางบริเวณซอย 5',
      details: 'มีกลิ่นเหม็นสะสมและมีคราบน้ำขยะตกค้างบนผิวถนนหลังรถเทศบาลเข้าจัดเก็บ',
      status: 'resolved',
      createdAt: '08/08/2026 14:20',
      assignedTo: 'ทีมแม่บ้านและงานดูแลสิ่งแวดล้อม',
      resolution: 'ดำเนินการฉีดน้ำยาฆ่าเชื้อและขัดล้างพื้นจุดพักขยะสะอาดเรียบร้อยแล้ว',
    },
    {
      id: 'cmp-4',
      ticketNo: 'CMP-2026-0044',
      houseNumber: '1086/02',
      residentName: 'คุณศิริพร บุญช่วย',
      category: 'ไฟส่องสว่าง',
      title: 'ไฟกิ่งถนนปากซอย 2 ดับ 1 ดวง',
      details: 'ช่วงค่ำปากซอยมืด เกรงว่าจะเกิดอันตรายต่อผู้สัญจรเดินเท้า',
      status: 'pending',
      createdAt: '16/08/2026 19:30',
      assignedTo: 'ช่างไฟฟ้าโครงการ',
      resolution: '',
    },
  ]);

  // 4. EDM state
  const [edmCampaigns] = useState<EdmCampaign[]>([
    {
      id: 'edm-1',
      subject: 'แจ้งเตือน: กำหนดการล้างทำความสะอาดถังพักน้ำประปาส่วนกลาง ประจำเดือนสิงหาคม',
      targetGroup: 'ลูกบ้านทุกแปลง (280 หลัง)',
      sentDate: '10/08/2026 10:00',
      status: 'sent',
      openedCount: 245,
      totalRecipients: 280,
      channel: 'LINE Official',
    },
    {
      id: 'edm-2',
      subject: 'หนังสือเชิญร่วมประชุมใหญ่สามัญเจ้าของร่วม ประจำปี 2569 (AGM 2026)',
      targetGroup: 'เจ้าของกรรมสิทธิ์ห้องชุด/บ้าน',
      sentDate: '01/08/2026 09:00',
      status: 'sent',
      openedCount: 268,
      totalRecipients: 280,
      channel: 'Email',
    },
    {
      id: 'edm-3',
      subject: 'แจ้งปิดปรับปรุงสระว่ายน้ำส่วนกลางเพื่อเปลี่ยนสารกรองทราย 22-23 ส.ค.',
      targetGroup: 'ลูกบ้านและผู้พักอาศัยทุกท่าน',
      sentDate: '15/08/2026 14:00',
      status: 'sent',
      openedCount: 210,
      totalRecipients: 280,
      channel: 'App Push',
    },
  ]);

  // 5. Legacy Repairs state
  const [legacyRepairs] = useState<LegacyRepairItem[]>([
    {
      id: 'leg-1',
      legacyCode: 'FIX-2021-089',
      houseNumber: '1086/01',
      residentName: 'คุณวีรวิช ทิพยชัยวรุตม์',
      title: 'ติดตั้งแอร์เพิ่มชั้น 2 และเดินท่อระบายน้ำทิ้งใหม่',
      category: 'งานแอร์และไฟฟ้า',
      technician: 'ช่างสมชาย (Sub-contractor A)',
      cost: 4500,
      completedDate: '18/12/2021',
      note: 'ตรวจสอบการระบายน้ำทิ้งไม่รั่วซึม รับประกันงานติดตั้ง 6 เดือน',
    },
    {
      id: 'leg-2',
      legacyCode: 'FIX-2022-014',
      houseNumber: '1086/05',
      residentName: 'คุณกานดา วงศ์สุริยา',
      title: 'เปลี่ยนก๊อกน้ำและซ่อมแซมวาล์วน้ำฝังผนังห้องน้ำชั้นล่าง',
      category: 'งานประปา',
      technician: 'ช่างประสิทธิ์ (ทีมช่างโครงการ)',
      cost: 1200,
      completedDate: '24/02/2022',
      note: 'เปลี่ยนอะไหล่วาล์วทองเหลืองแท้ ทดสอบแรงดันน้ำปกติ',
    },
    {
      id: 'leg-3',
      legacyCode: 'FIX-2022-105',
      houseNumber: '1086/12',
      residentName: 'คุณอนุกูล พรหมดี',
      title: 'ตรวจสอบระบบไฟกริ่งหน้าบ้านและสวิตช์ไฟรั้ว',
      category: 'งานไฟฟ้า',
      technician: 'ช่างวิรัช',
      cost: 650,
      completedDate: '19/07/2022',
      note: 'แก้ไขสายไฟหลวมที่กล่องพักสายหน้าบ้าน',
    },
  ]);

  // 6. Facilities state
  const [facilities] = useState<FacilityItem[]>([
    {
      id: 'fac-1',
      name: 'สระว่ายน้ำส่วนกลางระบบเกลือ (Infinity Edge Pool)',
      type: 'Outdoor Sport & Leisure',
      capacity: '30 ท่าน',
      openHours: '06:00 - 21:00 น.',
      status: 'available',
      fee: 'ฟรีสำหรับลูกบ้าน',
      location: 'ชั้น 1 อาคารคลับเฮาส์',
      bookingsToday: 8,
    },
    {
      id: 'fac-2',
      name: 'ห้องออกกำลังกาย & ฟิตเนส (Fitness Center)',
      type: 'Indoor Sport',
      capacity: '20 ท่าน',
      openHours: '06:00 - 21:30 น.',
      status: 'available',
      fee: 'ฟรีสำหรับลูกบ้าน',
      location: 'ชั้น 2 อาคารคลับเฮาส์',
      bookingsToday: 15,
    },
    {
      id: 'fac-3',
      name: 'ห้องประชุมและ Co-Working Space',
      type: 'Meeting & Business',
      capacity: '12 ที่นั่ง (มีโปรเจคเตอร์ & Wi-Fi)',
      openHours: '08:30 - 20:00 น.',
      status: 'available',
      fee: 'ชั่วโมงแรกฟรี (ชั่วโมงถัดไป 100 บ.)',
      location: 'ชั้น 1 อาคารนิติบุคคล',
      bookingsToday: 2,
    },
    {
      id: 'fac-4',
      name: 'สนามพัตต์กอล์ฟและสวนหย่อมสุขภาพ (Zen Garden)',
      type: 'Outdoor Garden',
      capacity: 'ไม่จำกัด',
      openHours: '05:30 - 20:00 น.',
      status: 'available',
      fee: 'ฟรีสำหรับลูกบ้าน',
      location: 'สวนสาธารณะส่วนกลางเฟส 1',
      bookingsToday: 6,
    },
  ]);

  // 7. Committee state
  const [committeeMeetings] = useState<CommitteeMeeting[]>([
    {
      id: 'com-1',
      meetingNo: 'ครั้งที่ 8/2569',
      title: 'การประชุมคณะกรรมการนิติบุคคล พิจารณางบประมาณปรับปรุงระบบกล้อง CCTV และไฟโซลาร์เซลล์',
      date: '25/08/2026',
      time: '19:00 - 21:00 น.',
      venue: 'ห้องประชุมชั้น 2 นิติบุคคล และผ่านระบบ Zoom',
      attendeesCount: 7,
      status: 'upcoming',
      resolutionsCount: 3,
      agendaItems: [
        'รับรองรายงานการประชุมครั้งที่ 7/2569',
        'พิจารณาใบเสนอราคาติดตั้งไฟถนนโซลาร์เซลล์รอบโครงการ',
        'พิจารณาคัดเลือกบริษัทรักษาความปลอดภัยและทำความสะอาดรายใหม่',
        'รายงานสถานะการเงินและการจัดเก็บค่าส่วนกลางประจำเดือน',
      ],
    },
    {
      id: 'com-2',
      meetingNo: 'ครั้งที่ 7/2569',
      title: 'การประชุมคณะกรรมการนิติบุคคล พิจารณาผลการตรวจนับทรัพย์สินประจำไตรมาส',
      date: '22/07/2026',
      time: '19:00 - 21:30 น.',
      venue: 'ห้องประชุมนิติบุคคล',
      attendeesCount: 6,
      status: 'completed',
      resolutionsCount: 4,
      agendaItems: [
        'อนุมัติงบซ่อมแซมปั๊มน้ำแรงดันสูงสระว่ายน้ำ 18,500 บาท',
        'อนุมัติมาตรการระเบียบการจอดรถของลูกบ้านและผู้มาติดต่อ',
        'รับทราบรายงานข้อร้องเรียนและการแก้ไขปัญหาของนิติบุคคล',
      ],
    },
  ]);

  // Parcel search & filter
  const [parcelSearch, setParcelSearch] = useState('');
  const filteredParcels = parcels.filter(
    (p) =>
      p.house_number.includes(parcelSearch) ||
      p.resident_name.toLowerCase().includes(parcelSearch.toLowerCase()) ||
      p.tracking_number.toLowerCase().includes(parcelSearch.toLowerCase())
  );

  // Maintenance tickets filter
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const filteredTickets = maintenanceTickets.filter((t) => {
    if (ticketStatusFilter === 'all') return true;
    return t.status === ticketStatusFilter;
  });

  return (
    <div className="space-y-4">
      {/* ========================================================================= */}
      {/* TOP SUB-NAVIGATION BAR (Exact match to Image 2, 3, 4, 5) */}
      {/* ========================================================================= */}
      {currentView !== 'menu' && (
        <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-4 sm:gap-6 overflow-x-auto text-xs text-slate-600 shrink-0 font-medium">
          <button
            type="button"
            onClick={() => setCurrentView('announcements')}
            className={`whitespace-nowrap transition-colors py-1 cursor-pointer ${
              currentView === 'announcements'
                ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                : 'hover:text-slate-900'
            }`}
          >
            ข่าวประชาสัมพันธ์
          </button>

          <button
            type="button"
            onClick={() => setCurrentView('surveys')}
            className={`whitespace-nowrap transition-colors py-1 cursor-pointer ${
              currentView === 'surveys'
                ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                : 'hover:text-slate-900'
            }`}
          >
            แบบสอบถาม
          </button>

          <button
            type="button"
            onClick={() => setCurrentView('parcels')}
            className={`whitespace-nowrap transition-colors py-1 cursor-pointer ${
              currentView === 'parcels'
                ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                : 'hover:text-slate-900'
            }`}
          >
            จดหมาย/พัสดุ
          </button>

          <button
            type="button"
            onClick={() => setCurrentView('complaints')}
            className={`whitespace-nowrap transition-colors py-1 cursor-pointer ${
              currentView === 'complaints'
                ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                : 'hover:text-slate-900'
            }`}
          >
            ร้องเรียน
          </button>

          <button
            type="button"
            onClick={() => setCurrentView('edm')}
            className={`whitespace-nowrap transition-colors py-1 cursor-pointer ${
              currentView === 'edm'
                ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                : 'hover:text-slate-900'
            }`}
          >
            EDM
          </button>

          <button
            type="button"
            onClick={() => setCurrentView('repairs_legacy')}
            className={`whitespace-nowrap transition-colors py-1 cursor-pointer ${
              currentView === 'repairs_legacy'
                ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                : 'hover:text-slate-900'
            }`}
          >
            แจ้งซ่อม (เก่า)
          </button>

          {/* แจ้งซ่อม Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setTopNavRepairsOpen(!topNavRepairsOpen)}
              className={`whitespace-nowrap transition-colors py-1 flex items-center gap-1 cursor-pointer ${
                currentView === 'repairs'
                  ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                  : 'hover:text-slate-900'
              }`}
            >
              <span>แจ้งซ่อม</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {topNavRepairsOpen && (
              <div className="absolute left-0 mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg p-1 z-30 space-y-1">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('repairs');
                    setTopNavRepairsOpen(false);
                  }}
                  className="w-full text-left px-3 py-1.5 rounded-lg hover:bg-slate-50 text-slate-700"
                >
                  รายการแจ้งซ่อม
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('repairs_legacy');
                    setTopNavRepairsOpen(false);
                  }}
                  className="w-full text-left px-3 py-1.5 rounded-lg hover:bg-slate-50 text-slate-700"
                >
                  ประวัติระบบเดิม (Legacy)
                </button>
              </div>
            )}
          </div>

          {/* พื้นที่ส่วนกลาง Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setTopNavFacilitiesOpen(!topNavFacilitiesOpen)}
              className={`whitespace-nowrap transition-colors py-1 flex items-center gap-1 cursor-pointer ${
                currentView === 'facilities'
                  ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                  : 'hover:text-slate-900'
              }`}
            >
              <span>พื้นที่ส่วนกลาง</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {topNavFacilitiesOpen && (
              <div className="absolute left-0 mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg p-1 z-30 space-y-1">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('facilities');
                    setTopNavFacilitiesOpen(false);
                  }}
                  className="w-full text-left px-3 py-1.5 rounded-lg hover:bg-slate-50 text-slate-700"
                >
                  สิ่งอำนวยความสะดวก
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setCurrentView('committee')}
            className={`whitespace-nowrap transition-colors py-1 cursor-pointer ${
              currentView === 'committee'
                ? 'font-bold text-slate-900 border-b-2 border-slate-900'
                : 'hover:text-slate-900'
            }`}
          >
            งานกรรมการ
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 1. PRIMARY 2-COLUMN HUB VIEW (Exact match to Image 1) */}
      {/* ========================================================================= */}
      {currentView === 'menu' && (
        <div className="min-h-[70vh] flex flex-col items-center justify-start pt-6 pb-16 px-4 bg-slate-50/50 rounded-2xl">
          {/* Centered Page Title matching Image 1 */}
          <div className="w-full max-w-4xl text-center mb-8">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
              งานเจ้าของร่วม/สมาชิก
            </h1>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              ศูนย์รวมบริการข้อมูล ข่าวสาร การสื่อสาร และงานบริการสำหรับลูกบ้านและเจ้าของร่วม
            </p>
          </div>

          {/* 2-Column Grid matching Image 1 */}
          <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {/* ----------------- COLUMN 1 (LEFT) ----------------- */}
            <div className="space-y-4 sm:space-y-4">
              {/* 1.1 ข่าวประชาสัมพันธ์ */}
              <button
                type="button"
                onClick={() => setCurrentView('announcements')}
                className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3.5">
                  <div className="text-amber-500 flex items-center justify-center shrink-0">
                    <Newspaper className="w-5 h-5 stroke-[1.8]" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                    ข่าวประชาสัมพันธ์
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    {announcements.length} ประกาศ
                  </span>
                </div>
              </button>

              {/* 1.2 แบบสอบถาม */}
              <button
                type="button"
                onClick={() => setCurrentView('surveys')}
                className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3.5">
                  <div className="text-amber-500 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 stroke-[1.8]" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                    แบบสอบถาม
                  </span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
                  {surveys.filter((s) => s.status === 'active').length} กำลังเปิดโหวต
                </span>
              </button>

              {/* 1.3 จดหมาย/พัสดุ */}
              <button
                type="button"
                onClick={() => setCurrentView('parcels')}
                className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3.5">
                  <div className="text-amber-500 flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 stroke-[1.8]" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                    จดหมาย/พัสดุ
                  </span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {parcels.filter((p) => p.status === 'arrived').length} รอมารับ
                </span>
              </button>

              {/* 1.4 ร้องเรียน */}
              <button
                type="button"
                onClick={() => setCurrentView('complaints')}
                className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3.5">
                  <div className="text-amber-500 flex items-center justify-center shrink-0">
                    <MessageSquare className="w-5 h-5 stroke-[1.8]" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                    ร้องเรียน
                  </span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                  {complaints.filter((c) => c.status !== 'resolved').length} รอดำเนินการ
                </span>
              </button>

              {/* 1.5 EDM */}
              <button
                type="button"
                onClick={() => setCurrentView('edm')}
                className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3.5">
                  <div className="text-amber-500 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 stroke-[1.8]" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                    EDM
                  </span>
                </div>
                <span className="text-xs text-slate-400 group-hover:text-slate-600">
                  Broadcast & Email →
                </span>
              </button>
            </div>

            {/* ----------------- COLUMN 2 (RIGHT) ----------------- */}
            <div className="space-y-4 sm:space-y-4">
              {/* 2.1 แจ้งซ่อม (เก่า) */}
              <button
                type="button"
                onClick={() => setCurrentView('repairs_legacy')}
                className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3.5">
                  <div className="text-amber-500 flex items-center justify-center shrink-0">
                    <Wrench className="w-5 h-5 stroke-[1.8]" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                    แจ้งซ่อม (เก่า)
                  </span>
                </div>
                <span className="text-xs text-slate-400 group-hover:text-slate-600">
                  ฐานข้อมูลประวัติเดิม
                </span>
              </button>

              {/* 2.2 แจ้งซ่อม (Dropdown / Modern) */}
              <div className="relative">
                <div
                  onClick={() => setCurrentView('repairs')}
                  className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="text-amber-500 flex items-center justify-center shrink-0">
                      <Wrench className="w-5 h-5 stroke-[1.8]" />
                    </div>
                    <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                      แจ้งซ่อม
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRepairsDropdownOpen(!repairsDropdownOpen);
                    }}
                    className="p-1 text-slate-500 hover:text-slate-800 rounded transition-colors"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${repairsDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Dropdown Menu for Repairs */}
                {repairsDropdownOpen && (
                  <div className="absolute right-0 left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-2 z-20 space-y-1 animate-in fade-in slide-in-from-top-1">
                    <button
                      type="button"
                      onClick={() => {
                        setRepairsDropdownOpen(false);
                        setCurrentView('repairs');
                      }}
                      className="w-full text-left px-3 py-2 text-xs rounded-lg hover:bg-slate-50 text-slate-700 flex items-center justify-between font-medium"
                    >
                      <span>📋 รายการแจ้งซ่อมทั้งหมด ({maintenanceTickets.length})</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRepairsDropdownOpen(false);
                        setCurrentView('repairs_legacy');
                      }}
                      className="w-full text-left px-3 py-2 text-xs rounded-lg hover:bg-slate-50 text-slate-700 flex items-center justify-between font-medium"
                    >
                      <span>🗄️ คลังประวัติการซ่อมระบบเก่า (Legacy)</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  </div>
                )}
              </div>

              {/* 2.3 พื้นที่ส่วนกลาง (Dropdown) */}
              <div className="relative">
                <div
                  onClick={() => setCurrentView('facilities')}
                  className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="text-amber-500 flex items-center justify-center shrink-0">
                      <Building2 className="w-5 h-5 stroke-[1.8]" />
                    </div>
                    <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                      พื้นที่ส่วนกลาง
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFacilitiesDropdownOpen(!facilitiesDropdownOpen);
                    }}
                    className="p-1 text-slate-500 hover:text-slate-800 rounded transition-colors"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${facilitiesDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Dropdown Menu for Facilities */}
                {facilitiesDropdownOpen && (
                  <div className="absolute right-0 left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-2 z-20 space-y-1 animate-in fade-in slide-in-from-top-1">
                    <button
                      type="button"
                      onClick={() => {
                        setFacilitiesDropdownOpen(false);
                        setCurrentView('facilities');
                      }}
                      className="w-full text-left px-3 py-2 text-xs rounded-lg hover:bg-slate-50 text-slate-700 flex items-center justify-between font-medium"
                    >
                      <span>🏊 สิ่งอำนวยความสะดวก & พื้นที่ส่วนกลาง (4 แห่ง)</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  </div>
                )}
              </div>

              {/* 2.4 งานกรรมการ */}
              <button
                type="button"
                onClick={() => setCurrentView('committee')}
                className="w-full bg-white hover:bg-slate-50/90 border border-slate-200/90 rounded-xl px-5 py-4 flex items-center justify-between shadow-2xs hover:shadow-xs transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3.5">
                  <div className="text-amber-500 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 stroke-[1.8]" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                    งานกรรมการ
                  </span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {committeeMeetings.length} วาระการประชุม
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. SUB-VIEW: ข่าวประชาสัมพันธ์ (Exact match to Images 2, 3, 4, 5, 6, 7) */}
      {/* ========================================================================= */}
      {currentView === 'announcements' && (
        <AdminNewsManagementView onBackToMenu={() => setCurrentView('menu')} />
      )}

      {/* ========================================================================= */}
      {/* 3. SUB-VIEW: แบบสอบถาม (Exact match to User's Images 1, 2, 3, 4, 5) */}
      {/* ========================================================================= */}
      {currentView === 'surveys' && (
        <AdminSurveyManagementView onBackToMenu={() => setCurrentView('menu')} />
      )}

      {/* ========================================================================= */}
      {/* 4. SUB-VIEW: จดหมาย/พัสดุ (Exact match to User's 17 Images) */}
      {/* ========================================================================= */}
      {currentView === 'parcels' && (
        <AdminMailParcelManagementView
          onBackToMenu={() => setCurrentView('menu')}
          onNavigateSubView={(v) => setCurrentView(v as MembersAffairsSubView)}
        />
      )}

      {/* ========================================================================= */}
      {/* 5. SUB-VIEW: ร้องเรียน (Complaints & Petitions - กล่องข้อความ) */}
      {/* ========================================================================= */}
      {currentView === 'complaints' && (
        <AdminComplaintsManagementView onBackToMenu={() => setCurrentView('menu')} />
      )}

      {/* ========================================================================= */}
      {/* 6. SUB-VIEW: EDM (Direct Mail & Broadcast) */}
      {/* ========================================================================= */}
      {currentView === 'edm' && (
        <div className="space-y-6">
          <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentView('menu')}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                title="กลับสู่เมนูหลัก"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  ระบบสื่อสารข้อความและการตลาด (EDM & Broadcast Hub)
                </h2>
                <p className="text-xs text-slate-500">
                  ส่งข้อความบรอดแคสต์ อีเมล และแจ้งเตือน Push Notification ถึงลูกบ้านทุกหลัง
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => alert('เปิดตัวช่วยสร้างแคมเปญ EDM บรอดแคสต์')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer shrink-0"
            >
              <Send className="w-4 h-4" /> ส่งข้อความบรอดแคสต์ใหม่
            </button>
          </div>

          {/* Campaign List */}
          <div className="space-y-4">
            {edmCampaigns.map((camp) => (
              <div key={camp.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-2xs space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                      {camp.channel}
                    </span>
                    <span className="font-bold text-slate-900 text-sm sm:text-base">{camp.subject}</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                    ✅ ส่งออกสำเร็จ
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-slate-50 p-3.5 rounded-xl text-xs">
                  <div>
                    <span className="text-slate-400 block mb-0.5">กลุ่มเป้าหมายผู้รับ:</span>
                    <span className="font-semibold text-slate-800">{camp.targetGroup}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">วันที่และเวลาส่ง:</span>
                    <span className="font-semibold text-slate-800">{camp.sentDate}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">อัตราการเปิดอ่าน (Open Rate):</span>
                    <span className="font-bold text-emerald-700 font-mono">
                      {camp.openedCount} / {camp.totalRecipients} (
                      {Math.round((camp.openedCount / camp.totalRecipients) * 100)}%)
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 7. SUB-VIEW: แจ้งซ่อม (เก่า) (Legacy Repairs) */}
      {/* ========================================================================= */}
      {currentView === 'repairs_legacy' && (
        <div className="space-y-6">
          <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentView('menu')}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                title="กลับสู่เมนูหลัก"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-amber-500" />
                  ประวัติรายการแจ้งซ่อมระบบเดิม (Legacy Maintenance Archive)
                </h2>
                <p className="text-xs text-slate-500">
                  ฐานข้อมูลสถิติและประวัติการซ่อมบำรุงในอดีต (2020-2022)
                </p>
              </div>
            </div>

            <span className="text-xs text-slate-500 font-medium">
              ข้อมูลประวัติ {legacyRepairs.length} รายการ
            </span>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="p-3.5">รหัสแจ้งซ่อมเดิม</th>
                  <th className="p-3.5">บ้านเลขที่ / เจ้าของ</th>
                  <th className="p-3.5">รายการแจ้งซ่อม</th>
                  <th className="p-3.5">หมวดหมู่งาน</th>
                  <th className="p-3.5">ช่างผู้รับผิดชอบ</th>
                  <th className="p-3.5">ค่าใช้จ่าย</th>
                  <th className="p-3.5">วันที่แล้วเสร็จ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {legacyRepairs.map((rep) => (
                  <tr key={rep.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="p-3.5 font-mono font-bold text-slate-700">{rep.legacyCode}</td>
                    <td className="p-3.5">
                      <span className="font-bold text-slate-900 block">{rep.houseNumber}</span>
                      <span className="text-[11px] text-slate-500">{rep.residentName}</span>
                    </td>
                    <td className="p-3.5 font-medium text-slate-800">
                      <div>{rep.title}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{rep.note}</div>
                    </td>
                    <td className="p-3.5">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                        {rep.category}
                      </span>
                    </td>
                    <td className="p-3.5 text-slate-700 font-medium">{rep.technician}</td>
                    <td className="p-3.5 font-mono font-bold text-amber-700">
                      ฿{rep.cost.toLocaleString()}
                    </td>
                    <td className="p-3.5 text-slate-500">{rep.completedDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 8. SUB-VIEW: แจ้งซ่อม (Modern Repairs) */}
      {/* ========================================================================= */}
      {currentView === 'repairs' && (
        <div className="space-y-6">
          <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentView('menu')}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                title="กลับสู่เมนูหลัก"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-amber-500" />
                  ระบบแจ้งซ่อมและงานบำรุงรักษา (Maintenance & Repairs)
                </h2>
                <p className="text-xs text-slate-500">
                  จัดการใบแจ้งซ่อม มอบหมายงานช่าง ติดตามสถานะงานซ่อมส่วนบุคคลและส่วนกลาง
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={ticketStatusFilter}
                onChange={(e) => setTicketStatusFilter(e.target.value)}
                className="text-xs p-2 rounded-xl border border-slate-200 bg-white font-medium"
              >
                <option value="all">สถานะทั้งหมด ({maintenanceTickets.length})</option>
                <option value="pending">รอรับเรื่อง</option>
                <option value="in_progress">กำลังดำเนินการ</option>
                <option value="completed">ซ่อมเสร็จสิ้น</option>
              </select>
            </div>
          </div>

          {/* Tickets Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="p-3.5">บ้านเลขที่</th>
                  <th className="p-3.5">ชื่อลูกบ้าน</th>
                  <th className="p-3.5">รายการแจ้งซ่อม</th>
                  <th className="p-3.5">หมวดหมู่</th>
                  <th className="p-3.5">สถานะ</th>
                  <th className="p-3.5">วันที่แจ้ง</th>
                  <th className="p-3.5 text-right">การจัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTickets.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="p-3.5 font-bold font-mono text-slate-900">{t.house_number}</td>
                    <td className="p-3.5 font-medium text-slate-800">{t.resident_name}</td>
                    <td className="p-3.5">
                      <div className="font-bold text-slate-900">{t.title}</div>
                      <div className="text-[11px] text-slate-500 truncate max-w-xs">{t.description}</div>
                    </td>
                    <td className="p-3.5">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                        {t.category}
                      </span>
                    </td>
                    <td className="p-3.5">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                          t.status === 'completed'
                            ? 'bg-emerald-100 text-emerald-800'
                            : t.status === 'in_progress'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-rose-100 text-rose-800'
                        }`}
                      >
                        {t.status === 'completed'
                          ? '✅ เสร็จสิ้น'
                          : t.status === 'in_progress'
                          ? '🔄 กำลังซ่อม'
                          : '⏳ รอช่างรับงาน'}
                      </span>
                    </td>
                    <td className="p-3.5 text-slate-500">
                      {new Date(t.created_at).toLocaleDateString('th-TH')}
                    </td>
                    <td className="p-3.5 text-right">
                      {t.status !== 'completed' ? (
                        <button
                          type="button"
                          onClick={() => updateMaintenanceStatus(t.id, 'completed')}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
                        >
                          บันทึกปิดงานซ่อม
                        </button>
                      ) : (
                        <span className="text-[11px] text-emerald-700 font-semibold">ปิดงานแล้ว</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 9. SUB-VIEW: พื้นที่ส่วนกลาง (Facilities) */}
      {/* ========================================================================= */}
      {currentView === 'facilities' && (
        <div className="space-y-6">
          <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentView('menu')}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                title="กลับสู่เมนูหลัก"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-amber-500" />
                  การบริหารจัดการพื้นที่ส่วนกลาง (Common Facilities)
                </h2>
                <p className="text-xs text-slate-500">
                  ตรวจสอบสิ่งอำนวยความสะดวก การจองใช้งาน และระเบียบการใช้สถานที่ส่วนกลาง
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => alert('เปิดระบบจองพื้นที่ส่วนกลางสำหรับลูกบ้าน')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4" /> บันทึกการจองใหม่
            </button>
          </div>

          {/* Facility Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {facilities.map((fac) => (
              <div key={fac.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-2xs space-y-3">
                <div className="flex items-start justify-between gap-2 border-b border-slate-100 pb-3">
                  <div>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                      {fac.type}
                    </span>
                    <h3 className="font-bold text-slate-900 text-sm sm:text-base mt-1">{fac.name}</h3>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                    🟢 พร้อมเปิดใช้งาน
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-400 block text-[11px]">เวลาเปิด-ปิด:</span>
                    <span className="font-semibold text-slate-800">{fac.openHours}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">ความจุสูงสุด:</span>
                    <span className="font-semibold text-slate-800">{fac.capacity}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">อัตราค่าบริการ:</span>
                    <span className="font-semibold text-emerald-700">{fac.fee}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">ที่ตั้งสถานที่:</span>
                    <span className="font-semibold text-slate-800">{fac.location}</span>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-medium">
                    การจองวันนี้: <strong className="text-slate-800">{fac.bookingsToday} รายการ</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => alert(`เปิดตารางปฏิทินการจอง: ${fac.name}`)}
                    className="text-sky-600 hover:underline font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    <Calendar className="w-3.5 h-3.5" /> ดูตารางปฏิทิน
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 10. SUB-VIEW: งานกรรมการ (Committee Affairs) */}
      {/* ========================================================================= */}
      {currentView === 'committee' && (
        <div className="space-y-6">
          <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentView('menu')}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                title="กลับสู่เมนูหลัก"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  งานคณะกรรมการนิติบุคคล (Board & Committee Affairs)
                </h2>
                <p className="text-xs text-slate-500">
                  จัดการวาระการประชุม บันทึกมติคณะกรรมการ และเอกสารคำสั่งนิติบุคคล
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => alert('เปิดระบบนัดหมายการประชุมคณะกรรมการชุดใหม่')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4" /> บันทึกนัดหมายการประชุม
            </button>
          </div>

          {/* Committee Meetings List */}
          <div className="space-y-4">
            {committeeMeetings.map((m) => (
              <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-2xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono bg-amber-50 text-amber-800 border border-amber-200">
                        {m.meetingNo}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          m.status === 'upcoming'
                            ? 'bg-sky-100 text-sky-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {m.status === 'upcoming' ? '📅 กำหนดการประชุมข้างหน้า' : '✅ ประชุมเสร็จสิ้นแล้ว'}
                      </span>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm sm:text-base">{m.title}</h3>
                  </div>

                  <div className="text-xs text-slate-500">
                    <span className="font-semibold text-slate-800">{m.date}</span> ({m.time})
                  </div>
                </div>

                <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-100 space-y-2 text-xs">
                  <span className="font-semibold text-slate-700 block">ระเบียบวาระการประชุม:</span>
                  <ul className="list-disc list-inside space-y-1 text-slate-600">
                    {m.agendaItems.map((agenda, i) => (
                      <li key={i}>{agenda}</li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                  <span className="text-slate-500">
                    สถานที่: <strong>{m.venue}</strong> ({m.attendeesCount} ท่าน)
                  </span>
                  <button
                    type="button"
                    onClick={() => alert(`ดาวน์โหลดรายงานการประชุม: ${m.meetingNo}`)}
                    className="text-sky-600 hover:underline font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" /> รายงานการประชุม & มติกรรมการ (PDF)
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
